import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { DateTime, Interval } from 'luxon';

import config from '@/config';
import { TemplatePadi } from '@/config.type';
import { ALERT_TIMEOUT_KEYS } from '@/constants';

import {
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_KEY,
	AUTH_SUBJECT_KEY,
} from '@/views/home/auth/Authentication.hooks';

const NEXT_REMINDER_TIME_KEY = 'next-reminder-time';
const HARDSTOP_TIME_KEY = 'hard-stop-time';

export function useAuth(): [boolean, Dispatch<SetStateAction<boolean>>] {
	const [show, setShow] = useState(false);
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const exp = localStorage.getItem(AUTH_EXPIRATION_DATE_KEY);
	const sub = localStorage.getItem(AUTH_SUBJECT_KEY);
	const template = config.app.template as TemplatePadi;

	const goToSSO = () => {
		if (
			config?.app?.auth?.enabled &&
			config.app?.auth.type === 'date-token'
		) {
			return;
		}
		setShow(true);
		/*const message = 'You need to authenticate to keep reading';
		const redirectUrl = [
			template.config.ssoUrl,
			`?client_id=${template.config.clientId}&`,
			`redirect_uri=${location.origin}/callback&`,
			`extraData=${location.pathname}`
		].join('');
		alert(message);
		location.href = redirectUrl;*/
	};

	useEffect(() => {
		if (
			config?.app?.auth?.enabled &&
			config.app?.auth.type === 'date-token'
		) {
			return;
		}
		if (template.config.auth?.enabled) {
			handlePaywall(goToSSO);
			return;
		}

		if (!isAuthenticated || !exp || !sub) {
			goToSSO();
			return;
		}

		const expirationDateTime = DateTime.fromJSDate(new Date(`${exp}`));

		const currentDateTime = DateTime.fromJSDate(new Date());

		// This is for the session not for the subscription
		const isValid: boolean = currentDateTime <= expirationDateTime;

		if (isValid) {
			return;
		}

		goToSSO();
	}, [show]);

	return [show, setShow];
}

function handlePaywall(cb: () => void) {
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	if (isAuthenticated) {
		// If it's authenticated remove the limitations
		if (localStorage.getItem(NEXT_REMINDER_TIME_KEY)) {
			localStorage.removeItem(NEXT_REMINDER_TIME_KEY);
		}
		if (localStorage.getItem(HARDSTOP_TIME_KEY)) {
			localStorage.removeItem(HARDSTOP_TIME_KEY);
		}
		return;
	}
	const template = config.app.template as TemplatePadi;
	const c = template.config.auth?.config;
	if (!c) {
		return;
	}

	/*
		1. Check if hardtime exist
			1. If it doesn't exist set it
			2. If exist check if its later
				1. If it's expired redirect automatically
		2. Check if next reminder exist
			1. If it doesnt exist create it, and return
			2. If it doess check if its later
				1. If it is redirect
				2. If its not, change next time and redirect
	*/

	const { hardStopTime, reminderTime } = c;
	const now = DateTime.now();
	let nextReminderDT: DateTime;
	let hardStopDT: DateTime;

	// Check hard stop if we need to keep redirect
	const hardStop = localStorage.getItem(HARDSTOP_TIME_KEY);
	if (!hardStop) {
		hardStopDT = now.plus({ seconds: hardStopTime });
		localStorage.setItem(HARDSTOP_TIME_KEY, `${hardStopDT.toJSON()}`);
	} else {
		hardStopDT = DateTime.fromJSDate(new Date(`${hardStop}`));

		if (now >= hardStopDT) {
			AlertTimeout.clear();
			cb();
			return;
		}
	}

	AlertTimeout.set(now, hardStopDT, cb);
	// redirectLater(hardStopDT, true);

	const nextReminderTime = localStorage.getItem(NEXT_REMINDER_TIME_KEY);

	// If the reminder time is not set it means that is the first time
	if (!nextReminderTime) {
		nextReminderDT = now.plus({ seconds: reminderTime });
		localStorage.setItem(
			NEXT_REMINDER_TIME_KEY,
			`${nextReminderDT.toJSON()}`,
		);
		AlertTimeout.set(now, nextReminderDT, cb);
		// redirectLater(nextReminderDT);
		return;
	}

	nextReminderDT = DateTime.fromJSDate(new Date(`${nextReminderTime}`));

	if (now >= nextReminderDT) {
		nextReminderDT = now.plus({ seconds: reminderTime });
		localStorage.setItem(
			NEXT_REMINDER_TIME_KEY,
			`${nextReminderDT.toJSON()}`,
		);
		AlertTimeout.clear();
		cb();
	}

	AlertTimeout.set(now, nextReminderDT, cb);
	//redirectLater(nextReminderDT);
}

// TODO THIS IS ONLY APPLICABLE TO PADI
export async function verifySubscriber(id: string): Promise<boolean> {
	const template = config.app.template as TemplatePadi;
	const url = `${template.config.verificationUrl}?sub=${id}`;
	const res = await fetch(url);
	const { customerStatus } = await res.json();
	const { entitlementStatus, entitlementEndDate } = customerStatus;
	const activeEntitlementComponents = new Set([
		...customerStatus.activeEntitlementComponents,
	]);

	const hasEmags = activeEntitlementComponents.has('EMAGS');
	const isActive = entitlementStatus === 'ACTIVE';

	const active = hasEmags && isActive;

	if (active) {
		localStorage.setItem(AUTH_EXPIRATION_DATE_KEY, entitlementEndDate);
	}

	return active;
}

class AlertTimeout {
	static clear() {
		const id = sessionStorage.getItem(ALERT_TIMEOUT_KEYS.ID);
		if (id) {
			clearTimeout(parseInt(`${id}`));
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.ID);
		}

		const dt = sessionStorage.getItem(ALERT_TIMEOUT_KEYS.DATETIME);
		if (dt) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.DATETIME);
		}
	}

	static set(now: DateTime, later: DateTime, cb: () => void) {
		const id = sessionStorage.getItem(ALERT_TIMEOUT_KEYS.ID);
		const nto = sessionStorage.getItem(ALERT_TIMEOUT_KEYS.DATETIME);

		const diff = Interval.fromDateTimes(now, later);
		const diffMs = diff.length('milliseconds');
		// This means there isn't any timeout running
		if (!id) {
			if (!isNaN(diffMs)) {
				const id = setTimeout(() => {
					if (localStorage.getItem(AUTH_KEY)) {
						return;
					}
					AlertTimeout.clear();
					cb();
				}, diffMs);
				sessionStorage.setItem(ALERT_TIMEOUT_KEYS.ID, `${id}`);
				sessionStorage.setItem(
					ALERT_TIMEOUT_KEYS.DATETIME,
					`${later.toJSON()}`,
				);
			}
			return;
		}

		const nextTimeout = DateTime.fromJSDate(new Date(`${nto}`));
		const isNextTimeoutSooner = nextTimeout < later;
		if (isNextTimeoutSooner) {
			// If next timmeout is sooner that the new alert, ignore it
			return;
		}
		AlertTimeout.clear();
		if (!isNaN(diffMs)) {
			const id = setTimeout(() => {
				if (localStorage.getItem(AUTH_KEY)) {
					return;
				}
				AlertTimeout.clear();
				if (localStorage.getItem(NEXT_REMINDER_TIME_KEY)) {
					localStorage.removeItem(NEXT_REMINDER_TIME_KEY);
				}
				cb();
			}, diffMs);
			sessionStorage.setItem(ALERT_TIMEOUT_KEYS.ID, `${id}`);
			sessionStorage.setItem(
				ALERT_TIMEOUT_KEYS.DATETIME,
				`${later.toJSON()}`,
			);
		}
	}
}
